import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { mixpanelTrack } from '@/renderer/utils/stat/mixp';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { ArrowRightIcon } from '@heroicons/react/16/solid';
import { ArrowTopRightOnSquareIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { RocketLaunchIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';

export default WhoIsRASForPage;

export function WhoIsRASFor() {
  const {
    config: { title },
  } = usePageContext();

  useEffect(() => {
    mixpanelTrack('View WhoIsRASFor Page');
  }, []);

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Who is RAS for?" className="py-2 sm:hidden" />
      <div className="overflow-auto">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-2xl py-4 pt-0 text-center sm:pt-20 sm:pb-24">
            <h1 className="hidden font-bold text-3xl text-zinc-900 tracking-tight sm:block sm:text-4xl dark:text-zinc-200">
              {title}
            </h1>
          </div>
        </div>
        <div className="relative px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl space-y-5">
            <p>
              RAS is a rarity in the space, a product that gives you access to high level, pro
              quality, market influencing picks that consistently win and beat the closing line by
              wide margins.{' '}
              <strong>It’s the type of information that is typically never for sale.</strong>
            </p>
            <p>
              <strong>Make no mistake, the service isn’t for everyone</strong>. RAS is not for the
              casual bettor, the get rich quick parlay seekers, or smaller bettors with one or two
              outs. It’s not something you can give to a “normal” friend or family member and expect
              them to be successful. The product is for a tiny subset of obsessed bettors who meet a
              high bar.
            </p>
            <h2 className="py-5 font-semibold text-xl">
              There are two main types of bettors who get value from the service.
            </h2>
            <div className="space-y-5 rounded-sm bg-zinc-50 p-5 shadow-sm ring-1 ring-zinc-900/10 dark:bg-white/5 dark:ring-white/10">
              <div className="flex flex-row items-start space-x-4">
                <div className="rounded-full bg-emerald-500 p-2">
                  <TrophyIcon className="size-8 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">Pro and semi-pro bettors</h2>
                  <h3 className="italic">
                    Originators, market makers, high level top down bettors, arbitragers and movers.
                  </h3>
                </div>
              </div>
              <div className="md:pl-16">
                <p>These bettors:</p>
                <ul className="list-disc space-y-2 py-5 pl-5">
                  <li>
                    Are heavily invested and engaged in the NFL, CFB, or CBB side and total markets.
                  </li>
                  <li>Value market timing and can’t afford to be blindsided by big line moves.</li>
                  <li>
                    Often have unit sizes that are significantly higher than the cost of a RAS
                    subscription.
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-5 rounded-sm bg-zinc-50 p-5 shadow-sm ring-1 ring-zinc-900/10 dark:bg-white/5 dark:ring-white/10">
              <div className="flex flex-row items-start space-x-4">
                <div className="rounded-full bg-emerald-500 p-2">
                  <RocketLaunchIcon className="size-8 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">
                    Bettors who want the best actionable info money can buy without doing the work
                    themselves.
                  </h2>
                </div>
              </div>
              <div className="md:pl-16">
                <p>They don't want to:</p>
                <ul className="list-disc space-y-2 py-5 pl-5">
                  <li>Stare at an odds screen all day.</li>
                  <li>Spend hours researching and analyzing games.</li>
                  <li>Build and maintain their own models.</li>
                  <li>Keep track of relevant info on hundreds of players, coaches, and teams.</li>
                  <li>Give their accounts away to a betting partner.</li>
                </ul>
                <p>They just want to make good bets and improve their odds of winning.</p>
              </div>
            </div>
            <h3 className="py-5 font-semibold text-lg">
              In both cases, there are still big hurdles to overcome.
            </h3>
            <p>To utilize RAS successfully, bettors must have:</p>
            <ul className="space-y-4 pl-0">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="size-6 shrink-0 text-emerald-500" />
                <span>Sustainable and/or replenishable outs</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="size-6 shrink-0 text-emerald-500" />
                <span>A large enough bankroll</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="size-6 shrink-0 text-emerald-500" />
                <span>
                  Enough betting acumen to extract value from our releases in one or more of the
                  ways mentioned above
                </span>
              </li>
            </ul>
            <p>
              The larger and more advanced the bettor is, the more efficiently they will utilize the
              service, and the easier it becomes to justify the cost. For many, it ultimately
              becomes a no brainer.
            </p>
            <hr className="my-8 border-zinc-900/10 border-t dark:border-white/10" />
            <div className="space-y-5">
              <p>
                <strong>If you’re someone who fits the description above</strong>, welcome to RAS.
                We’re excited to work with you!
              </p>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <a href="/register" className="flex items-center space-x-1 no-underline">
                    <span>Register for free</span>
                    <ArrowRightIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a href="/order" className="flex items-center space-x-1 no-underline">
                    <span>Browse our services</span>
                    <ArrowRightIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a href="/support-chat" className="flex items-center space-x-1 no-underline">
                    <span>Contact support</span>
                    <ArrowRightIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:admin@raspicks.com"
                    className="flex items-center space-x-1 no-underline"
                  >
                    <span>Email us at admin@raspicks.com</span>
                    <ArrowRightIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
              </ul>
              <p>
                We’d love to get to know you and answer any questions you might have about the
                service or the industry in general.
              </p>
            </div>
            <div className="space-y-5 pt-5">
              <h4>
                <strong>If you don't fit the description</strong>
              </h4>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <a href="/register" className="flex items-center space-x-1 no-underline">
                    <span>Sign up and get free picks</span>
                    <ArrowRightIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.substack.com/@raspicks"
                    className="flex items-center space-x-1 no-underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Follow us on Substack</span>
                    <ArrowTopRightOnSquareIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@raspicks"
                    className="flex items-center space-x-1 no-underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Follow us on YouTube</span>
                    <ArrowTopRightOnSquareIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.x.com/@raspicks"
                    className="flex items-center space-x-1 no-underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Follow us on X</span>
                    <ArrowTopRightOnSquareIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.tiktok.com/@raspicks"
                    className="flex items-center space-x-1 no-underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Follow us on TikTok</span>
                    <ArrowTopRightOnSquareIcon className="inline-block size-4 shrink-0" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function WhoIsRASForPage() {
  return <WhoIsRASFor />;
}
